import { FC, useMemo } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { Modal } from '@mui/material';
import { CloseIcon } from 'assets';
import { TUpdate } from 'domain/types';
import { Button, Input } from 'view/components';
import * as CS from 'view/modals/styled';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { useStateMachine } from 'little-state-machine';
import { updateFAQ } from 'view/pages/createJobPost/helpers/updateFAQ';

interface IFormInput {
  question: string;
  answer: string;
}

const CreateJobPostAddQuestionModalFields = ({ onClose, editingQuestionId }) => {
  const { watch } = useFormContext<IFormInput>();

  const questionValue = watch('question') || '';
  const answerValue = watch('answer') || '';

  return (
    <>
      <Input name="question" label="Question" marginbottom={16} />
      <Input name="answer" label="Answer" multiline rows={6} marginbottom={16} />

      <CS.ModalMainFooter>
        <Button
          variant="outlined"
          text="Cancel"
          onClick={onClose}
          disabled={false}
          style={{ width: '100%', minHeight: '50px' }}
        />
        <Button
          variant="contained"
          text={editingQuestionId ? 'Edit' : 'Add'}
          type="submit"
          disabled={!questionValue || !answerValue}
        />
      </CS.ModalMainFooter>
    </>
  );
};

const CreateJobPostAddQuestionModal: FC<{ hideCloseButton?: boolean }> = ({
  hideCloseButton = false,
}) => {
  const isOpenAddQuestionModal: boolean = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.isOpenAddQuestionModal
  );

  const screeningQuestions: any = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.screeningQuestions
  );

  const editingQuestionId: any = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.editingQuestionId
  );

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const { actions } = useStateMachine({
    actions: { updateFAQ },
  });

  const screeningQuestionObj = useMemo(() => {
    if (!screeningQuestions.length || !editingQuestionId) return null;
    return screeningQuestions.filter((el) => {
      return el.id === editingQuestionId;
    })?.[0];
  }, [editingQuestionId, screeningQuestions]);

  const onClose = () => {
    if (update) update({ isOpenAddQuestionModal: false, editingQuestionId: null });
  };

  const onSubmit = (formData: any) => {
    if (update)
      if (editingQuestionId) {
        const scrQue = screeningQuestions.map((item) => {
          return item.id === editingQuestionId ? { ...item, ...formData } : item;
        });
        update({
          screeningQuestions: scrQue,
          isOpenAddQuestionModal: false,
          editingQuestionId: null,
        });
        actions.updateFAQ({ faq: scrQue });
      } else {
        update({
          screeningQuestions: [...screeningQuestions, { id: Date.now(), ...formData }],
          isOpenAddQuestionModal: false,
        });
        actions.updateFAQ({ faq: [...screeningQuestions, { id: Date.now(), ...formData }] });
      }
  };

  const defaultValues = {
    question: screeningQuestionObj?.question || '',
    answer: screeningQuestionObj?.answer || '',
  };

  return (
    <Modal
      open={isOpenAddQuestionModal}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <CS.ModalMainWrapper>
        {!hideCloseButton ? (
          <CS.CloseIconBox onClick={onClose}>
            <CloseIcon />
          </CS.CloseIconBox>
        ) : null}
        <CS.ModalMainHeader style={{ marginBottom: '32px' }}>
          <CS.ModalMainHeaderTitle>
            {editingQuestionId ? 'Edit Question' : 'Add Question'}
          </CS.ModalMainHeaderTitle>
          <CS.ModalMainHeaderSubTitle>
            Help identify the best candidates by adding custom FAQ
          </CS.ModalMainHeaderSubTitle>
        </CS.ModalMainHeader>
        <FormContainer
          key={JSON.stringify(defaultValues)}
          defaultValues={defaultValues}
          onSuccess={onSubmit}
        >
          <CreateJobPostAddQuestionModalFields
            onClose={onClose}
            editingQuestionId={editingQuestionId}
          />
        </FormContainer>
      </CS.ModalMainWrapper>
    </Modal>
  );
};

export default CreateJobPostAddQuestionModal;
